import { useState, useEffect, useMemo } from "react";
import { Drawer, Button, Input, InputGroup, Chip, ScrollShadow, useOverlayState, toast } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { useSession, useUser } from "@clerk/react";
import { Check, Plus } from "lucide-react";
import { createMeetQuick as createMeetQuickApi } from "@/api/meets";
import { getMyConnections as getUserMembersApi } from "@/api/users";
import { getCurrentTime, getNextAvailable15MinTime } from "@/utils/dates";
import MemberAvatar from "@/components/members/member-avatar";
import CreateMemberDrawer from "@/components/drawers/create-member-drawer";

const inputClass =
    "bg-white dark:bg-[#2C2C2C] border border-black/[0.2  ] hover:bg-surface-secondary rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-none text-foreground placeholder:text-muted";
const labelClass = "text-xs font-medium tracking-wide text-foreground";

const DesktopCreateQuickMeetsDrawer = ({ _show, _setShow, initialValues }) => {
  const navigate = useNavigate();
  const { session } = useSession();
  const { user } = useUser();

  const [eventName, setEventName] = useState("");
  const [loading, setLoading] = useState(false);

  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [membersLoading, setMembersLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [participants, setParticipants] = useState((initialValues && initialValues?.participants) ?? []);

  console.log(participants)

  const state = useOverlayState({
    isOpen: _show,
    onOpenChange: (isOpen) => { if (!isOpen) _setShow(false); },
  });

  useEffect(() => {
    if (!session || !_show) return;
    getMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, _show]);

  async function getMembers() {
    setMembersLoading(true);
    try {
      const token = await session.getToken();
      const _resp = await getUserMembersApi(token);
      if (_resp.data.Result === "OK") {
        const records = (_resp.data.Records || []).filter(
          (rec) => String(rec.id) !== String(user?.id)
        );
        setAllUsers(records);
      } else {
        setAllUsers([]);
      }
    } catch {
      setAllUsers([]);
    } finally {
      setMembersLoading(false);
    }
  }

  const selectedIds = useMemo(
    () => new Set(participants.map((p) => String(p.id))),
    [participants]
  );

  const filteredUsers = useMemo(() => {
    if (membersLoading) return [];
    if (!search.trim()) return allUsers;
    const q = search.toLowerCase();
    return allUsers.filter(
      (u) => u.fullname?.toLowerCase().includes(q) || u.email_address?.toLowerCase().includes(q)
    );
  }, [allUsers, search, membersLoading]);

  const toggleMember = (member) => {
    const id = String(member.id);
    const isSelected = selectedIds.has(id);
    setParticipants((prev) =>
      isSelected ? prev.filter((p) => String(p.id) !== id) : [...prev, member]
    );
  };

  function onCreateMemberDrawerResp(_cmd) {
    if (_cmd === "close") {
      setShowCreate(false);
      getMembers();
    }
  }

  function resetState() {
    setEventName("");
    setParticipants([]);
    setSearch("");
    state.close()
  }

  async function onCreateClick() {
    if (eventName.trim() === "") {
      toast.warning('Meeting Name is required')

      return
    };

    setLoading(true);
    const token = await session.getToken();
    const current_time = getCurrentTime();
    const end_time = getNextAvailable15MinTime();
    const payload = {
      title: eventName.trim(),
      start_time: current_time,
      end_time: end_time,
      // API doesn't support participants yet — harmless to include, wire up once it does.
      members: participants.map((p) => p.id),
    };

    createMeetQuickApi(payload, token)
      .then((_resp) => {
        if (_resp?.data?.Result === "SUCCESS") {
          const short_code = _resp.data.ShortCode;
          resetState();
          navigate(`/meetings/join/${short_code}`);
        } else {
          toast.danger("Could not create meeting", {
            description: "Please check the details and try again.",
          });
        }
      })
      .catch((err) => {
        console.error(err);
        toast.danger("Error", {
          description: "Something went wrong while creating the meeting.",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Drawer state={state}>
      <Drawer.Backdrop variant="blur" className="bg-black/30">
        <Drawer.Content placement="right">
          <Drawer.Dialog className="rounded-b-0 dark:bg-black pb-16 min-w-120">
            <Drawer.Header className="flex items-start gap-3">
              <Drawer.Heading className="flex flex-col gap-3">
                <div className="flex flex-col text-4xl font-black leading-[38px]">
                  <span className="text-accent/60 dark:text-white">Start a</span>
                  <span className="text-accent">Quick Meet</span>
                </div>

                <span className="text-sm text-muted">Spin one up now, invite others in seconds.</span>
              </Drawer.Heading>
            </Drawer.Header>

            <Drawer.Body className="pb-6 flex flex-col gap-4 relative z-10">
              {/* Event name */}
              <div className="flex flex-col gap-2">
                <label className={labelClass}>
                  Meeting Name <span className="text-danger text-sm">*</span>
                </label>
                <Input
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  type="text"
                  placeholder="Quick Sync"
                  className={inputClass}
                />
              </div>

              {/* Quick add participants */}
              <div className="flex flex-col gap-2">
                <label className={labelClass}>Add Participants (optional)</label>

                <InputGroup className={inputClass}>
                  <InputGroup.Input
                    placeholder="Search people..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <InputGroup.Suffix>
                    <Button
                      isIconOnly
                      size="sm"
                      className="bg-accent-soft text-accent dark:bg-[#FFFFFF21] h-6 w-6 mr-1"
                      onClick={() => setShowCreate(true)}
                    >
                      <Plus size={16} strokeWidth={1} className="text-accent dark:text-white" />
                    </Button>
                  </InputGroup.Suffix>
                </InputGroup>

                {participants.length > 0 && (
                  <ScrollShadow hideScrollBar className="w-full flex py-0.5" orientation="horizontal">
                    {participants.map((member) => (
                      <button type="button" key={member.id} onClick={() => toggleMember(member)} className="shrink-0">
                        <Chip className="me-1 bg-accent/15 font-medium border-accent border-1 text-accent dark:bg-[#FFFFFF2B] dark:text-white dark:border-white dark:border-1 flex items-center gap-1">
                          <span className="capitalize">{member.fullname?.split(" ")[0]}</span>
                          <Check size={12} />
                        </Chip>
                      </button>
                    ))}
                  </ScrollShadow>
                )}

                <div className="flex flex-col gap-2 max-h-52 overflow-y-auto">
                  {membersLoading && <p className="text-sm text-muted text-center py-4">Loading people...</p>}

                  {!membersLoading && filteredUsers.length === 0 && (
                    <p className="text-sm text-muted text-center py-4">No matches found.</p>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {!membersLoading &&
                      filteredUsers.map((person) => {
                        const isSelected = selectedIds.has(String(person.id));
                        return (
                          <button
                            type="button"
                            key={person.id}
                            onClick={() => toggleMember(person)}
                            className={[
                              "flex gap-2 items-center px-2.5 cursor-pointer hover:bg-accent-soft hover:border-accent transition-colors duration-500 py-2.5 rounded-2xl border transition-colors text-left",
                              isSelected
                                ? "bg-accent/10 border-accent/30 dark:bg-white/8 dark:border-white/40"
                                : "bg-white border-black/[0.2] dark:bg-[#2C2C2C]",
                            ].join(" ")}
                          >
                            <MemberAvatar member={person} color={"success"} />
                            <span className="font-bold capitalize text-sm grow">{person.fullname}</span>
                            {isSelected && <Check size={16} className="text-accent dark:text-white shrink-0" />}
                          </button>
                        );
                      })}

                  </div>
                </div>
              </div>
            </Drawer.Body>

            <Drawer.Footer className="gap-3 relative z-10">
              <Button
                onPress={() => _setShow(false)}
                className="w-1/3 bg-background text-foreground"
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                onPress={onCreateClick}
                isPending={loading}
                isDisabled={loading}
                className="w-full disabled:opacity-100"
                variant="primary"
              >
                {loading ? "Creating..." : "Create"}
              </Button>
            </Drawer.Footer>

            {/* <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 drawer-blob" /> */}
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>

      <CreateMemberDrawer show={showCreate} onResp={onCreateMemberDrawerResp} />
    </Drawer>
  );
};

export default DesktopCreateQuickMeetsDrawer;